import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Lock, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSurfLevel, useSetSurfLevel, useAchievements, useToggleAchievement } from '@/hooks/useSurfProgression';
import {
  SURF_LEVELS,
  ACHIEVEMENTS_BY_LEVEL,
  categoriesForLevel,
  mandatoryCountForLevel,
  isPrerequisiteLevel,
  type SurfLevel,
} from '@/lib/surf-progression';
import SetSurfLevelCard from '@/components/dashboard/SetSurfLevelCard';
import AchievementRow from '@/components/progression/AchievementRow';

const SurfingProgression = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const { data: level, isLoading: levelLoading } = useSurfLevel();
  const { data: completedIds = new Set<string>(), isLoading: achievementsLoading } = useAchievements();
  const setLevelMutation = useSetSurfLevel();
  const toggleAchievementMutation = useToggleAchievement();
  const hydrated = !levelLoading && !achievementsLoading;
  const [activeTab, setActiveTab] = useState<SurfLevel | null>(null);

  const tab = activeTab ?? level ?? 'Absolute Beginner';

  if (loading) {
    return (
      <Layout>
        <Seo title="My Surfing Progression" description="Your surfing achievements and progression." path="/my-swell-breaks/progression" noIndex />
        <div className="container mx-auto px-4 py-20 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <Seo title="My Surfing Progression" description="Your surfing achievements and progression." path="/my-swell-breaks/progression" noIndex />
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4">
            <Card className="max-w-lg mx-auto border-border/50">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Lock className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="font-serif text-2xl">Sign in to continue</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground text-center">
                  Track your surfing progression and achievements once you're signed in.
                </p>
                <div className="flex flex-col gap-3 pt-2">
                  <Button variant="default" className="w-full" onClick={() => navigate('/auth', { state: { from: location.pathname } })}>
                    Create Account
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => navigate('/auth', { state: { from: location.pathname } })}>
                    Sign In
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Seo title="My Surfing Progression" description="Your surfing achievements and progression." path="/my-swell-breaks/progression" noIndex />

      <section className="pt-8 pb-6 md:pt-12">
        <div className="container mx-auto px-4 max-w-3xl">
          <Link
            to="/my-swell-breaks"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            My Swell Breaks
          </Link>
          <h1 className="font-serif text-2xl md:text-4xl font-semibold text-foreground">My Surfing Progression</h1>
        </div>
      </section>

      <section className="pb-16 md:pb-20">
        <div className="container mx-auto px-4 max-w-3xl">
          {!hydrated ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !level ? (
            <SetSurfLevelCard onSelect={(next) => setLevelMutation.mutate(next)} />
          ) : (
            <Tabs value={tab} onValueChange={(v) => setActiveTab(v as SurfLevel)}>
              <TabsList className="w-full flex-wrap h-auto gap-1">
                {SURF_LEVELS.map((lvl) => (
                  <TabsTrigger key={lvl} value={lvl} className="flex-1 min-w-[100px] text-xs sm:text-sm">
                    {lvl}
                  </TabsTrigger>
                ))}
              </TabsList>

              {SURF_LEVELS.map((lvl) => {
                const achievements = ACHIEVEMENTS_BY_LEVEL[lvl];
                const mandatoryCount = mandatoryCountForLevel(lvl);
                const completedCount = achievements.filter((a) => !a.optional && completedIds.has(a.id)).length;
                const progressPct = mandatoryCount > 0 ? Math.round((completedCount / mandatoryCount) * 100) : 0;
                const categories = categoriesForLevel(lvl);

                // A level below the user's self-selected starting level is a
                // satisfied prerequisite, not "0 achievements done" -- no
                // individual completion records are generated for it (per
                // the product brief), this is purely a derived display state.
                if (isPrerequisiteLevel(lvl, level)) {
                  return (
                    <TabsContent key={lvl} value={lvl} className="mt-6">
                      <div className="rounded-2xl border border-border/50 bg-card px-6 py-10 text-center">
                        <CheckCircle2 className="h-8 w-8 text-primary mx-auto mb-3" />
                        <h2 className="font-serif text-xl text-foreground">{lvl}</h2>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Completed prerequisite level — you told us you're past this when you set your level.
                        </p>
                      </div>
                    </TabsContent>
                  );
                }

                return (
                  <TabsContent key={lvl} value={lvl} className="mt-6">
                    <div className="mb-6">
                      <div className="flex items-baseline justify-between mb-2">
                        <h2 className="font-serif text-xl text-foreground">{lvl}</h2>
                        <span className="text-sm text-muted-foreground tabular-nums">
                          {completedCount} / {mandatoryCount} · {progressPct}%
                        </span>
                      </div>
                      <Progress value={progressPct} className="h-2" />
                    </div>

                    <Accordion type="multiple" defaultValue={[categories[0]]} className="space-y-2">
                      {categories.map((category) => {
                        const inCategory = achievements.filter((a) => a.category === category);
                        const doneInCategory = inCategory.filter((a) => completedIds.has(a.id)).length;
                        return (
                          <AccordionItem
                            key={category}
                            value={category}
                            className="border border-border/50 rounded-lg px-4 bg-card"
                          >
                            <AccordionTrigger className="hover:no-underline">
                              <span className="flex items-center gap-2 text-sm sm:text-base">
                                {category}
                                <span className="text-xs text-muted-foreground tabular-nums font-normal">
                                  {doneInCategory}/{inCategory.length}
                                </span>
                              </span>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div>
                                {inCategory.map((achievement) => (
                                  <AchievementRow
                                    key={achievement.id}
                                    achievement={achievement}
                                    completed={completedIds.has(achievement.id)}
                                    onToggle={() =>
                                      toggleAchievementMutation.mutate({
                                        achievementId: achievement.id,
                                        completed: !completedIds.has(achievement.id),
                                      })
                                    }
                                  />
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </TabsContent>
                );
              })}
            </Tabs>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default SurfingProgression;
